/**
 * SignalsRowsTable
 * ================
 *
 * "Rows" view of the Data Signals overview. Built on the shared
 * ``DataTable`` so density / hover / sort affordance match the
 * Trainings, Pipelines, and Models pages.
 *
 * Sort + pagination are handled by the parent (this view shares the
 * same client-side sorted slice that the Grid / Compact views use,
 * so the user sees the same ordering across all three modes).
 */

import React, { createContext, useContext, useMemo } from 'react';
import {
  Box, Checkbox, Chip, CircularProgress, IconButton, LinearProgress, Stack,
  Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Numbers as NumbersIcon,
  Category as CategoryIcon,
  ToggleOn as ToggleOnIcon,
  Schedule as ScheduleIcon,
  TextFields as TextFieldsIcon,
  Tag as TagIcon,
  Place as PlaceIcon,
  Layers as LayersIcon,
  TrackChanges as TrackChangesIcon,
  Repeat as RepeatIcon,
  Warning as WarningIcon,
  DeleteOutline as DeleteOutlineIcon,
  Storefront as StorefrontIcon,
  AddBusiness as AddBusinessIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
  Bolt as BoltIcon,
  Add as AddIcon,
  EditOutlined as EditOutlinedIcon,
  StraightenOutlined as StraightenOutlinedIcon,
} from '@mui/icons-material';
import { DiscoveryIcon } from '../global/CustomIcons';
import { useNavigate } from 'react-router-dom';
import DataTable from '../common/DataTable';
import MiniSparkline from './MiniSparkline';
import EventMiniSparkline from './EventMiniSparkline';
import {
  formatLargeNumber, formatNumber, secondsToHumanReadable,
} from '../../utils/utils';
import { familyOf, hasAnomalies, isEventLike } from './SignalCard';
import { useCanOpenPublishFlow } from '../marketplace/marketplaceUtils';

const FAMILY_META = {
  event:       { label: 'Event',       icon: BoltIcon,       tone: 'error'     },
  numeric:     { label: 'Numeric',     icon: NumbersIcon,    tone: 'primary'   },
  categorical: { label: 'Categorical', icon: CategoryIcon,   tone: 'secondary' },
  binary:      { label: 'Binary',      icon: ToggleOnIcon,   tone: 'info'      },
  time:        { label: 'Time',        icon: ScheduleIcon,   tone: 'success'   },
  text:        { label: 'Text',        icon: TextFieldsIcon, tone: 'warning'   },
  id:          { label: 'Identifier',  icon: TagIcon,        tone: 'neutral'   },
  spatial:     { label: 'Spatial',     icon: PlaceIcon,      tone: 'success'   },
  mixed:       { label: 'Mixed',       icon: LayersIcon,     tone: 'neutral'   },
};

const toneColor = (theme, tone) => {
  switch (tone) {
    case 'primary':   return theme.palette.primary.main;
    case 'secondary': return theme.palette.secondary.main;
    case 'success':   return theme.palette.success.main;
    case 'info':      return theme.palette.info.main;
    case 'warning':   return theme.palette.warning.main;
    case 'error':     return theme.palette.error.main;
    default:          return theme.palette.text.secondary;
  }
};

const ScoreBar = ({ value, color }) => {
  const theme = useTheme();
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return (
      <Typography variant="caption" color="text.disabled">—</Typography>
    );
  }
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center"
           sx={{ minWidth: 84 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          flex: 1, height: 5, borderRadius: 3,
          backgroundColor: alpha(color, 0.15),
          '& .MuiLinearProgress-bar': { backgroundColor: color },
        }}
      />
      <Typography variant="caption"
        sx={{ fontWeight: 700, color, width: 28, textAlign: 'right',
              fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(pct)}%
      </Typography>
    </Stack>
  );
};

const TrendChip = ({ direction, pct }) => {
  const theme = useTheme();
  if (!direction || direction === 'flat' || pct === null
      || pct === undefined) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <TrendingFlatIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">flat</Typography>
      </Stack>
    );
  }
  const up = direction === 'up';
  const Icon = up ? TrendingUpIcon : TrendingDownIcon;
  const color = up ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <Icon fontSize="inherit" sx={{ color }} />
      <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
        {pct > 0 ? '+' : ''}{Math.round(pct)}%
      </Typography>
    </Stack>
  );
};

// ---- selection context -----------------------------------------------------
//
// Selection state is delivered to the checkbox cells through context, NOT
// through the ``columns`` array. This keeps ``columns`` referentially stable
// across selection changes, so DataTable's memoized rows skip re-rendering
// (a checkbox toggle repaints ~100 checkboxes instead of ~100 full rows with
// sparklines/tooltips — that was a visible ~1s delay).

const SelectionContext = createContext({
  selectedKeys: null,
  onToggleSelect: null,
  onToggleSelectPage: null,
  selectableRows: [],
});

const HeaderSelectAllCheckbox = () => {
  const {
    selectedKeys, onToggleSelectPage, selectableRows,
  } = useContext(SelectionContext);
  const selectedOnPage = selectableRows
    .filter((p) => selectedKeys?.has(p.key)).length;
  return (
    <Checkbox
      size="small"
      sx={{ p: 0 }}
      checked={selectableRows.length > 0
        && selectedOnPage === selectableRows.length}
      indeterminate={selectedOnPage > 0
        && selectedOnPage < selectableRows.length}
      onChange={() => onToggleSelectPage && onToggleSelectPage(selectableRows)}
      inputProps={{ 'aria-label': 'Select all rows on this page' }}
    />
  );
};

const RowSelectCheckbox = ({ row }) => {
  const { selectedKeys, onToggleSelect } = useContext(SelectionContext);
  return (
    <Checkbox
      size="small"
      sx={{ p: 0 }}
      checked={!!selectedKeys?.has(row.key)}
      onChange={() => onToggleSelect(row)}
      inputProps={{ 'aria-label': `Select ${row.key}` }}
    />
  );
};

// ---- helpers to derive stats matching SignalCard's compute pass ----

const _lastValue = (bins) => {
  if (!Array.isArray(bins)) return null;
  for (let i = bins.length - 1; i >= 0; i -= 1) {
    const b = bins[i];
    const c = Number(b?.count) || 0;
    const v = Number(b?.lastValue);
    if (c > 0 && Number.isFinite(v)) return v;
  }
  return null;
};

const _trend = (bins) => {
  if (!Array.isArray(bins) || bins.length < 3) {
    return { direction: 'flat', pct: null };
  }
  let sumI = 0, sumA = 0, sumII = 0, sumIA = 0, n = 0;
  let totalCount = 0, weightedAvg = 0;
  bins.forEach((b, i) => {
    const a = Number(b?.avgValue);
    const c = Number(b?.count) || 0;
    if (c > 0 && Number.isFinite(a)) {
      sumI += i; sumA += a; sumII += i * i; sumIA += i * a;
      n += 1;
      totalCount += c; weightedAvg += a * c;
    }
  });
  if (n < 3) return { direction: 'flat', pct: null };
  const meanI = sumI / n;
  const meanA = sumA / n;
  const denom = sumII - n * meanI * meanI;
  if (denom <= 0) return { direction: 'flat', pct: null };
  const slope = (sumIA - n * meanI * meanA) / denom;
  const totalChange = slope * (n - 1);
  const mean = totalCount > 0 ? weightedAvg / totalCount : null;
  if (!mean || Math.abs(mean) < 1e-9) return { direction: 'flat', pct: null };
  const pct = (totalChange / Math.abs(mean)) * 100;
  if (pct > 5)  return { direction: 'up',   pct };
  if (pct < -5) return { direction: 'down', pct };
  return { direction: 'flat', pct };
};

const _formatStat = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1000) return formatLargeNumber(v) || formatNumber(v);
  return Number(v).toLocaleString(undefined, {
    maximumFractionDigits: 2, minimumFractionDigits: 0,
  });
};

// ---- main table component ------------------------------------------------

const SignalsRowsTable = ({
  signals, relevance,
  onDelete, onPublish, onUnsubscribe, onOpen, onRecordEvent, onRename,
  onSetUnit,
  publishedByKey,
  // Set of signal keys with a delete request in flight - rendered
  // dimmed + with a "Deleting…" badge so the user sees activity and
  // doesn't fire a second click.
  deletingKeys = [],
  // Bulk selection (managed by the parent). When ``onToggleSelect`` is
  // provided, a leading checkbox column appears. Event streams ARE
  // selectable — the parent tracks which selected rows are events and
  // routes their deletion through the stream path (group actions are
  // disabled for them there). Toggle callbacks receive the full ROW so
  // the parent can capture that metadata.
  selectedKeys = null,
  onToggleSelect = null,
  onToggleSelectPage = null,
  // Group chip column (resolved by the BE). Clicking a chip filters the
  // list to that group via ``onGroupClick``.
  onGroupClick = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Publish affordance is capability-gated; published signals keep
  // their manage affordance so sellers can always delist.
  const canOpenPublishFlow = useCanOpenPublishFlow();
  const deletingSet = useMemo(
    () => new Set(deletingKeys || []),
    [deletingKeys],
  );

  const selectable = typeof onToggleSelect === 'function';
  const selectableRows = useMemo(
    () => (signals || []),
    [signals],
  );

  // Context value: changes on every selection toggle, but ONLY the
  // checkbox components consume it — the columns array below stays stable.
  const selectionCtx = useMemo(() => ({
    selectedKeys, onToggleSelect, onToggleSelectPage, selectableRows,
  }), [selectedKeys, onToggleSelect, onToggleSelectPage, selectableRows]);

  const columns = useMemo(() => [
    ...(selectable ? [{
      key: 'select',
      label: <HeaderSelectAllCheckbox />,
      width: 36,
      stopRowClick: true,
      render: (p) => <RowSelectCheckbox row={p} />,
    }] : []),
    {
      key: 'signal',
      label: 'Signal',
      width: 210,
      render: (p) => {
        const family = familyOf(p);
        const fam = FAMILY_META[family] || FAMILY_META.mixed;
        const FamIcon = fam.icon;
        const famColor = toneColor(theme, fam.tone);
        const isMkt = !!p.marketplace;
        const isEvent = family === 'event';
        const displayKey = p.display_name
          || (isEvent
            ? (p.name || p.text || 'Event')
            : (p.marketplace?.original_key || p.key));
        const types = (p.value_types || []).join(', ');
        const isDeleting = deletingSet.has(p.key);
        const isUnavailable = isMkt
          && p.marketplace?.data_status === 'unavailable';
        return (
          <Stack direction="row" spacing={1} alignItems="center"
                 sx={{
                   minWidth: 0,
                   opacity: isDeleting ? 0.5 : (isUnavailable ? 0.65 : 1),
                 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(famColor, 0.12), color: famColor, flexShrink: 0,
            }}>
              <FamIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={p.display_name
                  ? `${p.display_name} (key: ${p.key})`
                  : displayKey}>
                  {/* minWidth: 0 lets the name shrink inside the flex row so
                      noWrap can ellipsize instead of pushing the icons out. */}
                  <Typography fontWeight={700} noWrap
                              sx={{ fontSize: 13, minWidth: 0 }}>
                    {displayKey}
                  </Typography>
                </Tooltip>
                {onRename && (
                  <Tooltip title="Rename — sets the display name used across the platform">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onRename(p); }}
                      sx={{
                        p: 0.25,
                        color: 'text.disabled',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {isMkt && (
                  <Tooltip
                    title={`Marketplace · ${p.marketplace.seller_label || 'Seller'}`}
                  >
                    <StorefrontIcon sx={{
                      fontSize: 13, color: theme.palette.primary.main,
                    }} />
                  </Tooltip>
                )}
                {isMkt && p.marketplace.verified && (
                  <Tooltip title="Verified data provider">
                    <VerifiedIcon sx={{
                      fontSize: 13, color: theme.palette.primary.main,
                    }} />
                  </Tooltip>
                )}
                {isUnavailable && (
                  <Tooltip title={
                    `Seller has no data for this signal right now. `
                    + `Your subscription is active, but no signals are `
                    + `available - try again later or unsubscribe.`
                  }>
                    <Chip
                      size="small"
                      label="No data from seller"
                      icon={<ReportProblemOutlinedIcon
                        sx={{ fontSize: 12, ml: 0.5 }} />}
                      sx={{
                        ml: 0.5,
                        height: 18,
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: theme.palette.warning.main,
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        '& .MuiChip-label': { px: 0.75 },
                        '& .MuiChip-icon': {
                          color: theme.palette.warning.main,
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {isDeleting && (
                  <Chip
                    size="small"
                    label="Deleting…"
                    color="default"
                    icon={<CircularProgress size={10} sx={{ ml: 0.5 }} />}
                    sx={{
                      ml: 0.5,
                      height: 18,
                      fontSize: 10.5,
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', fontSize: 10.5 }}
                          noWrap>
                {isEvent
                  ? [
                    `${fam.label} · ${p.signal_count || 0} event${(p.signal_count === 1) ? '' : 's'}`,
                    (p.scheduled_count > 0) && `${p.scheduled_count} scheduled`,
                    isMkt && `by ${p.marketplace.seller_label}`,
                  ].filter(Boolean).join(' · ')
                  : `${fam.label}${types ? ` · ${types}` : ''}${isMkt ? ` · by ${p.marketplace.seller_label}` : ''}`}
              </Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      // Measurement unit ("$/MWh", "kWh", "%") — its own column, right
      // after the name it qualifies. The chip IS the edit affordance
      // (click → unit dialog); unlabeled signals show a ruler button.
      key: 'unit',
      label: 'Unit',
      width: 72,
      stopRowClick: true,
      render: (p) => {
        if (isEventLike(p)) {
          return (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        }
        if (!p.unit) {
          if (!onSetUnit) {
            return (
              <Typography variant="caption" color="text.disabled">—</Typography>
            );
          }
          return (
            <Tooltip title="Set the measurement unit (e.g. $/MWh, kWh, %)">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onSetUnit(p); }}
                sx={{
                  p: 0.25,
                  color: 'text.disabled',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <StraightenOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          );
        }
        const inferred = p.unit_origin === 'llm';
        return (
          <Tooltip title={
            `${inferred ? 'AI-inferred unit' : 'Measurement unit'}`
            + (onSetUnit ? ' — click to change' : '')
          }>
            <Chip
              size="small"
              label={p.unit}
              clickable={!!onSetUnit}
              onClick={onSetUnit
                ? (e) => { e.stopPropagation(); onSetUnit(p); }
                : undefined}
              variant={inferred ? 'outlined' : 'filled'}
              sx={{
                height: 18,
                fontSize: 10.5,
                fontWeight: 600,
                maxWidth: 66,
                ...(inferred
                  ? { color: 'text.secondary' }
                  : {
                      color: theme.palette.info.main,
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                    }),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'group',
      label: 'Group',
      width: 100,
      hideBelow: 1100,
      stopRowClick: true,
      render: (p) => {
        if (p.type === 'event' || !p.group) {
          return (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        }
        const isUser = p.group_origin === 'user';
        return (
          <Tooltip title={
            (isUser ? 'Assigned group' : `Auto-grouped (${p.group_origin || 'auto'})`)
            + ' — click to filter the list to this group'
          }>
            <Chip
              size="small"
              label={p.group}
              clickable={!!onGroupClick}
              onClick={onGroupClick ? () => onGroupClick(p.group) : undefined}
              variant={isUser ? 'filled' : 'outlined'}
              sx={{
                height: 18,
                fontSize: 10.5,
                fontWeight: 600,
                maxWidth: 100,
                ...(isUser
                  ? {
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                    }
                  : { color: 'text.secondary' }),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'flags',
      label: 'Flags',
      width: 76,
      hideBelow: 1100,
      render: (p) => {
        const isHigh = relevance && typeof relevance[p.key] === 'number'
                       && relevance[p.key] >= 0.8;
        const anom = hasAnomalies(p);
        const patterned = !!p?.seasonality?.seasonality;
        if (!isHigh && !anom && !patterned) {
          return (
            <Typography variant="caption" color="text.disabled">stable</Typography>
          );
        }
        // Icons mirror the Quality filter chips (and their colors):
        // target = High Relevance, warning = Anomalous, repeat =
        // Patterned (seasonal / cyclic).
        return (
          <Stack direction="row" spacing={0.25}>
            {isHigh && (
              <Tooltip title="High relevance (≥80%) — strongly related to your goals">
                <TrackChangesIcon
                  sx={{ fontSize: 16, color: theme.palette.warning.main }} />
              </Tooltip>
            )}
            {anom && (
              <Tooltip title="Anomalies detected">
                <WarningIcon
                  sx={{ fontSize: 16, color: theme.palette.error.main }} />
              </Tooltip>
            )}
            {patterned && (
              <Tooltip title="Seasonal / cyclic pattern detected">
                <RepeatIcon
                  sx={{ fontSize: 16, color: theme.palette.success.main }} />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      key: 'records',
      label: 'Records',
      width: 70,
      align: 'right',
      render: (p) => (
        <Typography variant="body2" sx={{
          fontVariantNumeric: 'tabular-nums', fontWeight: 600,
        }}>
          {formatLargeNumber(p.signal_count || 0)
            || formatNumber(p.signal_count || 0)}
        </Typography>
      ),
    },
    {
      key: 'span',
      label: 'Span',
      width: 88,
      hideBelow: 1280,
      render: (p) => (
        <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: 10, lineHeight: 1.3, display: 'block' }}>
          {p.total_duration > 0
            ? secondsToHumanReadable(p.total_duration)
            : '—'}
        </Typography>
      ),
    },
    {
      key: 'last',
      label: 'Last value',
      width: 88,
      align: 'right',
      render: (p) => {
        // Event streams: the most useful "next thing" is a pending
        // scheduled occurrence, not the last score bin.
        if (isEventLike(p) && p.scheduled_count > 0 && p.next_scheduled_at) {
          const next = new Date(Number(p.next_scheduled_at));
          return (
            <Tooltip title={`Next scheduled event: ${next.toLocaleString()}`}>
              <Chip
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                label={next.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                sx={{
                  height: 20, fontSize: 10.5, fontWeight: 600,
                  color: theme.palette.warning.main,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  '& .MuiChip-icon': { color: theme.palette.warning.main },
                }}
              />
            </Tooltip>
          );
        }
        const lv = _lastValue(p.bins);
        return (
          <Typography variant="body2" sx={{
            fontVariantNumeric: 'tabular-nums', fontWeight: 600,
          }}>
            {lv !== null ? _formatStat(lv) : '—'}
          </Typography>
        );
      },
    },
    {
      key: 'trend',
      label: 'Trend',
      width: 66,
      hideBelow: 1200,
      render: (p) => {
        const t = _trend(p.bins);
        return <TrendChip direction={t.direction} pct={t.pct} />;
      },
    },
    {
      key: 'sparkline',
      label: 'Distribution',
      width: 130,
      hideBelow: 1420,
      stopRowClick: true,
      // Events get a signed sentiment sparkline (positive up / negative down,
      // dots colored by sentiment); signals get the value distribution.
      render: (p) => (
        isEventLike(p)
          ? <EventMiniSparkline points={p.points} height={30} width={116} />
          : <MiniSparkline bins={p.bins} height={30} width={116} />
      ),
    },
    {
      key: 'relevance',
      label: 'Relevance',
      width: 112,
      render: (p) => {
        // Event streams: relevance = the discovery graph's verdict. A
        // stream with live edges demonstrably moves goals — show that
        // instead of a score bar it never gets.
        if (isEventLike(p)) {
          const e = p.edges;
          if (!e || !e.goals_affected) {
            return (
              <Typography variant="caption" color="text.disabled">
                no edges yet
              </Typography>
            );
          }
          const hl = e.half_life_s
            ? ` · effect half-life ${secondsToHumanReadable(e.half_life_s)}` : '';
          const weak = e.weakening ? ` · ${e.weakening} weakening` : '';
          return (
            <Tooltip title={`Validated event edges: this stream moves ${e.goals_affected} goal${e.goals_affected === 1 ? '' : 's'} (max lift ${formatNumber(e.max_lift)})${hl}${weak}`}>
              <Chip
                size="small"
                icon={<BoltIcon sx={{ fontSize: 12 }} />}
                label={`drives ${e.goals_affected} goal${e.goals_affected === 1 ? '' : 's'}`}
                sx={{
                  height: 20, fontSize: 10.5, fontWeight: 600,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  '& .MuiChip-icon': { color: theme.palette.error.main },
                }}
              />
            </Tooltip>
          );
        }
        return (
          <ScoreBar
            value={relevance?.[p.key]}
            color={
              (relevance && relevance[p.key] >= 0.8)
                ? theme.palette.warning.main
                : theme.palette.primary.main
            }
          />
        );
      },
    },
    {
      key: 'actions',
      // Compact "+" in the header to record a new event (replaces the old
      // page-header "Record an event" button). Tooltip explains it.
      label: onRecordEvent ? (
        <Tooltip title="Record an event">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onRecordEvent(); }}
            sx={{
              p: 0.25,
              color: theme.palette.primary.main,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
            }}
          >
            {/* lightning = event, + = record a new one */}
            <BoltIcon fontSize="small" />
            <AddIcon sx={{ fontSize: 13, ml: -0.25 }} />
          </IconButton>
        </Tooltip>
      ) : '',
      width: 88,
      align: 'right',
      stopRowClick: true,
      render: (p) => {
        const isMkt = !!p.marketplace;
        const published = (publishedByKey && p?.key) ? publishedByKey[p.key] : null;
        return (
          <Stack direction="row" spacing={0} justifyContent="flex-end">
            {familyOf(p) === 'numeric' && !isEventLike(p) && (
              <Tooltip title="What drives this? Run relationship discovery">
                <IconButton
                  size="small"
                  onClick={() => navigate(
                    `/goals/create?target=${encodeURIComponent(p.key)}`,
                  )}
                  sx={{ p: 0.5, color: 'text.secondary',
                        '&:hover': { color: 'primary.main' } }}
                >
                  <DiscoveryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isMkt && onPublish
              && (published || canOpenPublishFlow) && (
              <Tooltip title={published
                  ? `Published · ${published.subscribers_count || 0} subscriber${(published.subscribers_count || 0) === 1 ? '' : 's'}`
                  : 'Put on Marketplace'}>
                <IconButton
                  size="small"
                  onClick={() => onPublish(p, published)}
                  sx={{
                    p: 0.5,
                    color: published
                      ? theme.palette.success.main
                      : 'text.secondary',
                    bgcolor: published
                      ? alpha(theme.palette.success.main, 0.12)
                      : 'transparent',
                    '&:hover': {
                      color: published
                        ? theme.palette.success.dark
                        : 'primary.main',
                    },
                  }}
                >
                  {published
                    ? <StorefrontIcon fontSize="small" />
                    : <AddBusinessIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            {isMkt && onUnsubscribe && (
              <Tooltip title="Unsubscribe">
                <IconButton
                  size="small"
                  onClick={() => onUnsubscribe(p.marketplace.subscription_id)}
                  sx={{ p: 0.5, color: theme.palette.primary.main }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isMkt && onDelete && (
              deletingSet.has(p.key) ? (
                <Tooltip title="Delete in progress">
                  <Box sx={{
                    p: 0.5, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', width: 30, height: 30,
                  }}>
                    <CircularProgress size={14} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(p.key)}
                    sx={{ p: 0.5, color: 'text.secondary',
                          '&:hover': { color: 'error.main' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Stack>
        );
      },
    },
  ], [theme, relevance, navigate, canOpenPublishFlow,
      onDelete, onPublish, onUnsubscribe, publishedByKey, deletingSet,
      onRecordEvent, onRename, onSetUnit, selectable, onGroupClick]);

  return (
    <SelectionContext.Provider value={selectionCtx}>
      <DataTable
        columns={columns}
        rows={signals}
        getRowKey={(p) => p.key}
        dense
        // Fixed layout so long signal keys truncate with an ellipsis
        // (full name lives in the tooltip) instead of widening the
        // Signal column and shifting every other column.
        tableLayout="fixed"
        // Whole-row click opens the detail modal — cells flagged
        // ``stopRowClick`` (sparkline / actions) absorb the event so
        // the user can interact with them without triggering the modal.
        onRowClick={onOpen}
        emptyState={{
          title: 'No signals match your filters',
          message: 'Try clearing the family / quality filters.',
        }}
      />
    </SelectionContext.Provider>
  );
};

export default React.memo(SignalsRowsTable);
